/**
 * DemoModeBadge — a prominent, persistent orange banner shown while demo mode is
 * active, so the operator (and anyone watching) can ALWAYS tell the on-screen
 * data is synthetic and that nothing they do touches real data. Rendered once,
 * globally, next to the app tutorial overlay.
 *
 * Pinned to the top-center (below the status bar / notch via the safe-area inset),
 * non-interactive (`pointerEvents="none"`) so it never blocks taps, with a gentle
 * pulse to draw the eye. Uses the Happy brand orange (never blue) per the design
 * rules and shows a localized "Demo mode active" label.
 */

import React, {useEffect, useRef, useState} from 'react';
import {Animated, Easing, StyleSheet, Text, View} from 'react-native';
import {useSafeAreaInsets} from 'react-native-safe-area-context';
import {useTranslation} from 'react-i18next';
import {isDemoMode, subscribeDemoMode} from '../demo';

/**
 * @returns {React.ReactElement|null} the badge, or null when demo mode is off.
 */
const DemoModeBadge = () => {
    const [on, setOn] = useState(() => isDemoMode());
    const {t} = useTranslation();
    const insets = useSafeAreaInsets();
    const pulse = useRef(new Animated.Value(0)).current;

    useEffect(() => subscribeDemoMode(setOn), []);

    useEffect(() => {
        if (!on) {
            return undefined;
        }

        const loop = Animated.loop(
            Animated.sequence([
                Animated.timing(pulse, {
                    toValue: 1,
                    duration: 900,
                    easing: Easing.inOut(Easing.ease),
                    useNativeDriver: true,
                }),
                Animated.timing(pulse, {
                    toValue: 0,
                    duration: 900,
                    easing: Easing.inOut(Easing.ease),
                    useNativeDriver: true,
                }),
            ]),
        );

        loop.start();

        return () => loop.stop();
    }, [on, pulse]);

    if (!on) {
        return null;
    }

    const scale = pulse.interpolate({inputRange: [0, 1], outputRange: [1, 1.6]});
    const opacity = pulse.interpolate({inputRange: [0, 1], outputRange: [0.9, 0.2]});
    const topOffset = Math.max(insets.top, 8) + 6;

    return (
        <View style={[styles.wrap, {top: topOffset}]} pointerEvents="none">
            <View style={styles.pill}>
                <View style={styles.dotWrap}>
                    <Animated.View style={[styles.dotHalo, {transform: [{scale}], opacity}]} />
                    <View style={styles.dot} />
                </View>
                <Text style={styles.text} numberOfLines={1}>
                    {t('demo_mode_active', 'Demo mode active')}
                </Text>
            </View>
        </View>
    );
};

const styles = StyleSheet.create({
    wrap: {
        position: 'absolute',
        left: 0,
        right: 0,
        alignItems: 'center',
        zIndex: 9999,
        elevation: 9999,
    },
    pill: {
        flexDirection: 'row',
        alignItems: 'center',
        backgroundColor: '#F97316',
        paddingHorizontal: 16,
        paddingVertical: 9,
        borderRadius: 999,
        shadowColor: '#F97316',
        shadowOpacity: 0.5,
        shadowRadius: 12,
        shadowOffset: {width: 0, height: 3},
    },
    dotWrap: {
        width: 12,
        height: 12,
        alignItems: 'center',
        justifyContent: 'center',
        marginRight: 9,
    },
    dotHalo: {
        position: 'absolute',
        width: 12,
        height: 12,
        borderRadius: 6,
        backgroundColor: '#fff',
    },
    dot: {
        width: 8,
        height: 8,
        borderRadius: 4,
        backgroundColor: '#fff',
    },
    text: {
        color: '#fff',
        fontWeight: '900',
        fontSize: 13,
        letterSpacing: 1.1,
        textTransform: 'uppercase',
    },
});

export default DemoModeBadge;
